import type { GroupBase, StylesConfig } from 'react-select'
import type { DefaultTheme } from 'styled-components'

import { focusRing, radiusInput } from 'core/styles'

import type { SelectOption } from './components'

export const createSelectStyles = <IsMulti extends boolean, Value>(
  theme: DefaultTheme,
): StylesConfig<
  SelectOption<Value>,
  IsMulti,
  GroupBase<SelectOption<Value>>
> => {
  const borderColor = theme.border
  const hoverBackground = theme.hover
  const textColor = theme.text
  const mutedTextColor = theme.labelText
  const disabledTextColor = theme.disabledText
  const highlightColor = theme.highlight
  const backgroundColor = theme.background

  const baseContainerStyles = (provided: any, state: any) => {
    const { selectProps } = state || {}
    const { classNamePrefix } = selectProps || {}
    return {
      ...provided,
      fontSize: classNamePrefix === 'inline' ? '14px' : '16px',
    }
  }

  const baseControlStyles = (provided: any, state: any) => {
    const { isFocused, menuIsOpen, selectProps } = state || {}
    const { classNamePrefix } = selectProps || {}

    return {
      ...provided,
      alignItems: 'normal',
      cursor: 'pointer',
      backgroundColor,
      borderRadius: radiusInput,
      ...(isFocused || menuIsOpen
        ? {
            borderColor: `${highlightColor} !important`,
            boxShadow: focusRing,
          }
        : classNamePrefix === 'inline'
        ? {
            '&:hover': { borderColor: 'transparent' },
            borderColor: 'transparent',
            minHeight: '0',
          }
        : {
            borderColor,
            boxShadow: 'none',
            '&:hover': { borderColor },
          }),
    }
  }

  const baseDropdownIndicator = (provided: any, state: any) => {
    const { selectProps } = state || {}
    const { classNamePrefix } = selectProps || {}

    const stylesForInlineDropdown =
      classNamePrefix === 'inline' ? { padding: '0' } : {}

    return {
      ...provided,
      ...stylesForInlineDropdown,
    }
  }

  const baseIndicatorSeparatorStyles = (provided: any) => ({
    ...provided,
    display: 'none',
    paddingLeft: '0',
  })

  const borderAndMarginStyles = (position: 'Top' | 'Bottom') => ({
    [`margin${position}`]: '-4px',
    [`border${position}`]: '0',
    [`border${position}LeftRadius`]: '0',
    [`border${position}RightRadius`]: '0',
  })

  const baseMenuStyles = (
    provided: any,
    { selectProps, placement }: any = {},
  ) => ({
    ...provided,
    boxShadow: 'none',
    border: `1px solid ${highlightColor}`,
    ...borderAndMarginStyles(placement === 'bottom' ? 'Top' : 'Bottom'),
    ...(selectProps.classNamePrefix === 'inline' ? { borderWidth: '1px' } : {}),
    backgroundColor,
  })

  const baseMenuListStyles = (provided: any) => ({
    ...provided,
    '::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: '12px',
    },
    '::-webkit-scrollbar-thumb': {
      borderRadius: '8px',
      backgroundColor: borderColor,
      border: `3px solid ${backgroundColor}`,
    },
  })

  const baseMultiValueLabelStyles = (provided: any, state: any) => {
    const { selectProps } = state || {}
    const { wrap } = selectProps || {}
    const stylesForWrap = {
      whiteSpace: wrap ? 'wrap' : 'nowrap',
    }
    return {
      ...provided,
      ...stylesForWrap,
    }
  }

  const baseOptionStyles = (provided: any, state: any) => {
    const { isDisabled, isFocused, isSelected, selectProps } = state || {}
    const { classNamePrefix } = selectProps || {}

    const stylesForInlineDropdown =
      classNamePrefix === 'inline'
        ? {
            padding: '12px',
            fontSize: 'inherit',
            ':hover': {
              backgroundColor: hoverBackground,
            },
          }
        : { paddingLeft: '12px' }

    return {
      ...provided,
      display: 'flex',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: isFocused ? hoverBackground : null,
      color: isDisabled ? disabledTextColor : textColor,
      fontWeight: isSelected ? 500 : 400,
      ':hover': {
        backgroundColor: isDisabled && 'transparent',
      },
      svg: {
        fill: textColor,
      },
      padding: '8px',
      ...stylesForInlineDropdown,
    }
  }

  const basePlaceholderStyles = (provided: any, state: any) => {
    const { selectProps } = state || {}
    const { classNamePrefix } = selectProps || {}

    const stylesForInlineDropdown =
      classNamePrefix === 'inline' ? { fontSize: 'inherit' } : {}

    return {
      ...provided,
      position: 'relative',
      top: '0',
      transform: 'none',
      margin: '0',
      color: mutedTextColor,
      fontSize: '16px',
      whiteSpace: 'nowrap',
      ...stylesForInlineDropdown,
    }
  }

  const baseSingleValueStyles = (provided: any, state: any) => {
    const { selectProps } = state || {}
    const { classNamePrefix } = selectProps || {}

    const stylesForInlineDropdown =
      classNamePrefix === 'inline'
        ? {
            maxWidth: 'none',
            color: 'inherit',
          }
        : {}

    return {
      ...provided,
      display: 'flex',
      alignItems: 'center',
      marginLeft: '0',
      marginRight: '0',
      maxWidth: 'none',
      color: textColor,
      ...stylesForInlineDropdown,
    }
  }

  const baseInputStyles = (provided: any) => ({
    ...provided,
    color: textColor,
  })

  const baseValueContainerStyles = (provided: any, state: any) => {
    const { selectProps } = state || {}
    const { classNamePrefix, wrap } = selectProps || {}

    const stylesForInlineDropdown =
      classNamePrefix === 'inline'
        ? {
            fontSize: 'inherit',
            padding: '4px 0 4px 10px',
          }
        : {}

    const stylesForWrap = {
      flexWrap: wrap ? 'wrap' : 'nowrap',
    }

    return {
      ...provided,
      ...stylesForInlineDropdown,
      ...stylesForWrap,
    }
  }

  return {
    container: (provided, state) => baseContainerStyles(provided, state),
    control: (provided, state) => baseControlStyles(provided, state),
    dropdownIndicator: (provided, state) =>
      baseDropdownIndicator(provided, state),
    indicatorSeparator: (provided) => baseIndicatorSeparatorStyles(provided),
    menu: (provided, state) => baseMenuStyles(provided, state),
    menuList: (provided) => baseMenuListStyles(provided),
    multiValueLabel: (provided, state) =>
      baseMultiValueLabelStyles(provided, state),
    option: (provided, state) => baseOptionStyles(provided, state),
    placeholder: (provided, state) => basePlaceholderStyles(provided, state),
    singleValue: (provided, state) => baseSingleValueStyles(provided, state),
    input: (provided) => baseInputStyles(provided),
    valueContainer: (provided, state) =>
      baseValueContainerStyles(provided, state),
  }
}

export default createSelectStyles
